import {
  Activity,
  AlertTriangle,
  FileText,
  Heart,
  Phone,
  Pill,
  Shield,
  Stethoscope,
  User,
  type LucideIcon,
} from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import type { PatientReportForm } from '../../types/prf';
import type { DisclosurePrfAccessLog, DisclosurePrfDisclosureLog } from '../../types/disclosure';

type PrfData = Partial<PatientReportForm> & Record<string, unknown>;

type AttendingExtras = {
  attendingPersons?: Array<{ name: string; dealtWithPatient: boolean }>;
  didDrTreatAssess?: boolean;
  drName?: string;
  drPin?: string;
};

export interface PrfRecordFullViewProps {
  prfId: string;
  incidentId?: string;
  eventId?: string;
  submittedAt?: string;
  submittedBy?: string;
  data: PrfData;
  accessLogs?: DisclosurePrfAccessLog[];
  disclosureLogs?: DisclosurePrfDisclosureLog[];
  /** Disclosure / access justification banner */
  accessBanner?: {
    username?: string;
    reason: string;
    accessedAt: string;
    label?: string;
  };
  embedded?: boolean;
  showPrintHeader?: boolean;
  printUsername?: string;
}

function outcomeLabel(outcome?: string) {
  if (outcome === 'clear') return 'Screening clear';
  if (outcome === 'hcp_authorised') return 'HCP authorised';
  if (outcome === 'blocked') return 'Blocked — do not administer';
  return outcome || '—';
}

function renderField(label: string, value: unknown) {
  let display: string;
  if (value === undefined || value === null || value === '') display = '—';
  else if (Array.isArray(value)) display = value.length ? value.join(', ') : '—';
  else if (typeof value === 'boolean') display = value ? 'Yes' : 'No';
  else display = String(value);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[8px] uppercase tracking-wider text-muted-foreground font-semibold print:text-gray-600">
        {label}
      </span>
      <span className="text-sm text-foreground print:text-gray-800">{display}</span>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden print:bg-gray-50 print:border-l-4 print:border-l-purple-600 print:border-t print:border-r print:border-b print:border-gray-300 print:rounded-none print:mb-4 prf-print-section">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-accent/30 print:bg-purple-600 print:text-white print:border-b-0">
        <Icon className="w-4 h-4 text-primary print:text-white" />
        <h3 className="text-sm font-semibold text-foreground print:text-white">{title}</h3>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4 print:grid-cols-1 print:gap-3 print:p-4">{children}</div>
    </div>
  );
}

function parseAttendingExtras(attending: PrfData['attending']): AttendingExtras | null {
  try {
    if (attending?.additionalNotes?.trim().startsWith('{')) {
      return JSON.parse(attending.additionalNotes) as AttendingExtras;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export default function PrfRecordFullView({
  prfId,
  incidentId,
  eventId,
  submittedAt,
  submittedBy,
  data,
  accessLogs,
  disclosureLogs,
  accessBanner,
  embedded = false,
  showPrintHeader = false,
  printUsername,
}: PrfRecordFullViewProps) {
  const patient = data.patientDetails || {};
  const nok = data.nokDetails || {};
  const complaint = data.presentingComplaint || {};
  const pmh = data.pastMedicalHistory || {};
  const allergies = data.allergiesMeds || {};
  const fast = data.fast || {};
  const obs = data.observations || {};
  const attending = data.attending || {};
  const paracetamol = data.paracetamolScreening;
  const isParacetamolForm = data.formType === 'paracetamol_screening' || !!paracetamol;
  const isMinorInjuryForm = data.formType === 'minor_injury' || prfId.startsWith('MIF-');
  const isStandardForm = !isParacetamolForm && !isMinorInjuryForm;
  const minorInjuryNotes = data.minorInjuryNotes;
  const closingDetails = data.closingDetails || {};
  const hasClosingDetails =
    closingDetails.resultOutcome ||
    closingDetails.resultOutcomeLabel ||
    closingDetails.actionsTaken ||
    minorInjuryNotes;
  const attendingExtras = parseAttendingExtras(attending);

  const formTitle = isParacetamolForm
    ? 'Paracetamol Screening Record'
    : isMinorInjuryForm
      ? 'Minor Injury Form'
      : 'Patient Report Form';

  const fmt = (ts?: string) => (ts ? formatDateTime(ts) : '—');

  return (
    <div className={embedded ? 'space-y-4 prf-record-full-view' : 'space-y-4 prf-record-full-view print:p-4 print:bg-white print:font-sans print:text-gray-900'}>
      {showPrintHeader && (
        <div className="hidden print:flex print:flex-col print:bg-gradient-to-r print:from-purple-600 print:to-purple-800 print:text-white print:p-4 print:mb-4 print:rounded-t">
          <div className="print:flex print:justify-between print:text-xs print:opacity-90 print:mb-2">
            <span>Event ID: {eventId || '—'}</span>
            <span>Incident ID: {incidentId || '—'}</span>
            <span>PRF ID: {prfId}</span>
          </div>
          <div className="print:text-lg print:font-semibold print:tracking-wider">{formTitle}</div>
        </div>
      )}

      {embedded && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 print:border-purple-300 print:bg-purple-50">
          <p className="text-xs font-bold text-foreground print:text-gray-900">{formTitle}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground print:text-gray-700 mt-0.5">
            {eventId && <span>Event: {eventId}</span>}
            {incidentId && <span>Incident: {incidentId}</span>}
            <span className="font-mono">{prfId}</span>
            {submittedAt && <span>Submitted: {fmt(submittedAt)}</span>}
            {submittedBy && <span>By: {submittedBy}</span>}
          </div>
        </div>
      )}

      {accessBanner && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs print:border-amber-700 print:bg-amber-50 print:text-gray-900">
          <p className="font-semibold text-amber-200 print:text-amber-900 mb-1 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            {accessBanner.label || 'Access justification'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
            {accessBanner.username && (
              <div>
                <span className="text-muted-foreground print:text-gray-600">Username: </span>
                <span className="font-mono text-foreground print:text-gray-900">{accessBanner.username}</span>
              </div>
            )}
            <div>
              <span className="text-muted-foreground print:text-gray-600">Opened: </span>
              <span className="font-mono text-foreground print:text-gray-900">{fmt(accessBanner.accessedAt)}</span>
            </div>
            <div className="sm:col-span-3">
              <span className="text-muted-foreground print:text-gray-600">Reason: </span>
              <span className="text-foreground print:text-gray-900">{accessBanner.reason}</span>
            </div>
          </div>
        </div>
      )}

      <Section title="Patient Details" icon={User}>
        {renderField(isParacetamolForm ? 'Full Name' : 'First Name', patient.firstName)}
        {!isParacetamolForm && renderField('Last Name', patient.lastName)}
        {renderField('Date of Birth', patient.dateOfBirth)}
        {(isStandardForm || isMinorInjuryForm) && renderField('Age', patient.age)}
        {(isStandardForm || isMinorInjuryForm) && renderField('Gender', patient.gender)}
        {isStandardForm && renderField('NHS Number', patient.nhsNumber)}
        {renderField('Phone', patient.phoneNumber)}
        {isStandardForm && renderField('Address', patient.address)}
        {isStandardForm && renderField('Postcode', patient.postcode)}
      </Section>

      {isMinorInjuryForm && (
        <Section title="Next of Kin" icon={Phone}>
          {renderField('Name', nok.name)}
          {renderField('Relationship', nok.relationship)}
          {renderField('Phone', nok.phoneNumber)}
          {renderField('Address', nok.address)}
        </Section>
      )}

      {isParacetamolForm && paracetamol && (
        <Section title="Paracetamol Screening" icon={Pill}>
          {renderField('Outcome', outcomeLabel(paracetamol.outcome))}
          {renderField('Taken in last 4 hours', paracetamol.takenLast4Hours)}
          {renderField('Under 16', paracetamol.under16)}
          {renderField('Allergic to Paracetamol', paracetamol.allergic)}
          {renderField('GP/HCP restriction', paracetamol.gpRestricted)}
          {renderField('HCP approved', paracetamol.hcpApproved)}
          {paracetamol.hcpApproved && renderField('HCP name', paracetamol.hcpName)}
          {paracetamol.hcpApproved && renderField('Authorisation code', paracetamol.hcpAuthorisationCode)}
        </Section>
      )}

      {(isMinorInjuryForm || isParacetamolForm) && (complaint.chiefComplaint || complaint.additionalNotes) && (
        <Section title="Presenting Complaint" icon={AlertTriangle}>
          {renderField('Chief Complaint', complaint.chiefComplaint)}
          {isMinorInjuryForm && renderField('Onset Time', complaint.onsetTime)}
          {isMinorInjuryForm && renderField('Duration', complaint.duration)}
          {isMinorInjuryForm && renderField('Severity', complaint.severity)}
          {renderField('Notes', complaint.additionalNotes)}
        </Section>
      )}

      {isMinorInjuryForm && (
        <Section title="Attending" icon={Stethoscope}>
          {attendingExtras?.attendingPersons?.length
            ? attendingExtras.attendingPersons.map((person, i) => (
                <div key={i} className="col-span-2 md:col-span-3 space-y-2 pb-3 border-b border-border/50 last:border-0 last:pb-0">
                  {renderField(`Attending ${i + 1} — Name`, person.name)}
                  {renderField('Dealt with patient', person.dealtWithPatient)}
                </div>
              ))
            : (
              <>
                {renderField('Primary Clinician', attending.primaryClinician)}
                {renderField('Secondary Clinician', attending.secondaryClinician)}
              </>
            )}
          {renderField('Dr treated / assessed', attendingExtras?.didDrTreatAssess)}
          {attendingExtras?.didDrTreatAssess && renderField('Dr name', attendingExtras.drName)}
          {attendingExtras?.didDrTreatAssess && renderField('Dr PIN', attendingExtras.drPin)}
        </Section>
      )}

      {isMinorInjuryForm && hasClosingDetails && (
        <Section title="Closing Details" icon={FileText}>
          {renderField(
            'Result / Outcome',
            closingDetails.resultOutcomeLabel ||
              closingDetails.resultOutcome?.replace(/_/g, ' ') ||
              minorInjuryNotes,
          )}
          {renderField('Actions Taken', closingDetails.actionsTaken)}
        </Section>
      )}

      {isStandardForm && (
        <>
          <Section title="Next of Kin" icon={Phone}>
            {renderField('Name', nok.name)}
            {renderField('Relationship', nok.relationship)}
            {renderField('Phone', nok.phoneNumber)}
            {renderField('Address', nok.address)}
          </Section>

          <Section title="Presenting Complaint" icon={AlertTriangle}>
            {renderField('Chief Complaint', complaint.chiefComplaint)}
            {renderField('Onset Time', complaint.onsetTime)}
            {renderField('Duration', complaint.duration)}
            {renderField('Severity', complaint.severity)}
            {renderField('Symptoms', complaint.symptoms)}
            {renderField('Notes', complaint.additionalNotes)}
          </Section>

          <Section title="Past Medical History" icon={Heart}>
            {renderField('Conditions', pmh.conditions)}
            {renderField('Surgeries', pmh.surgeries)}
            {renderField('Hospitalisations', pmh.hospitalisations)}
            {renderField('Notes', pmh.additionalNotes)}
          </Section>

          <Section title="Allergies & Medications" icon={Pill}>
            {renderField('Allergies', allergies.allergies)}
            {renderField('Current Medications', allergies.currentMedications)}
            {renderField('Recent Medications', allergies.recentMedications)}
            {renderField('Notes', allergies.additionalNotes)}
          </Section>

          <Section title="F.A.S.T (Stroke Assessment)" icon={Activity}>
            {renderField('Face Drooping', fast.faceDropping)}
            {renderField('Arm Weakness', fast.armWeakness)}
            {renderField('Speech Difficulty', fast.speechDifficulty)}
            {renderField('Time of Onset', fast.timeOfOnset)}
            {renderField('FAST Positive', fast.fastPositive)}
            {renderField('Notes', fast.additionalNotes)}
          </Section>

          <Section title="Observations" icon={Activity}>
            {renderField('BP Systolic', obs.bloodPressureSystolic)}
            {renderField('BP Diastolic', obs.bloodPressureDiastolic)}
            {renderField('Heart Rate', obs.heartRate)}
            {renderField('Resp. Rate', obs.respiratoryRate)}
            {renderField('SpO2', obs.oxygenSaturation)}
            {renderField('Temperature', obs.temperature)}
            {renderField('Glucose', obs.glucoseLevel)}
            {renderField('Pain Score', obs.painScore)}
            {renderField('Consciousness', obs.consciousness)}
            {renderField('Time Recorded', obs.timeRecorded)}
            {renderField('Notes', obs.additionalNotes)}
          </Section>

          <Section title="Attending" icon={Stethoscope}>
            {renderField('Primary Clinician', attending.primaryClinician)}
            {renderField('Secondary Clinician', attending.secondaryClinician)}
            {renderField('Role', attending.role)}
            {renderField('Arrival Time', attending.arrivalTime)}
            {renderField('Departure Time', attending.departureTime)}
            {renderField('Transport Destination', attending.transportDestination)}
            {renderField('Handover To', attending.handoverTo)}
            {renderField('Notes', attending.additionalNotes)}
          </Section>

          {hasClosingDetails && (
            <Section title="Closing Details" icon={FileText}>
              {renderField(
                'Result / Outcome',
                closingDetails.resultOutcomeLabel ||
                  closingDetails.resultOutcome?.replace(/_/g, ' '),
              )}
              {renderField('Actions Taken', closingDetails.actionsTaken)}
            </Section>
          )}
        </>
      )}

      {(accessLogs?.length || disclosureLogs?.length) ? (
        <Section title="Record access & disclosure log" icon={Shield}>
          <div className="col-span-2 md:col-span-3 space-y-2">
            {accessLogs?.map((log) => (
              <div key={log.id} className="text-xs border-b border-border/50 pb-2 last:border-0 last:pb-0">
                <p className="font-mono font-semibold text-foreground print:text-gray-900">
                  Access: {log.username} · {fmt(log.accessedAt)}
                </p>
                <p className="text-muted-foreground print:text-gray-700 mt-0.5">{log.reason}</p>
              </div>
            ))}
            {disclosureLogs?.map((log) => (
              <div key={log.id} className="text-xs border-b border-border/50 pb-2 last:border-0 last:pb-0">
                <p className="font-mono font-semibold text-amber-400 print:text-amber-800">
                  Disclosed: {log.username} · {fmt(log.disclosedAt)}
                </p>
                <p className="text-muted-foreground print:text-gray-700 mt-0.5">{log.reason}</p>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {showPrintHeader && printUsername && (
        <div className="hidden print:flex print:flex-col print:gap-1 print:px-5 print:py-3 print:bg-gray-100 print:border-t print:border-gray-300 print:text-xs print:text-gray-600 print:mt-8">
          <div className="print:flex print:justify-between print:items-center">
            <span className="font-semibold text-red-600 print:text-red-600 print:uppercase print:tracking-wider">
              CONFIDENTIAL
            </span>
            <span>Printed by: {printUsername}</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface PaperPatientFullViewProps {
  prfId: string;
  formType: string;
  notes?: string;
  submittedAt?: string;
  submittedBy?: string;
  incidentId?: string;
  eventId?: string;
}

export function PaperPatientFullView({
  prfId,
  formType,
  notes,
  submittedAt,
  submittedBy,
  incidentId,
  eventId,
}: PaperPatientFullViewProps) {
  const title =
    formType === 'minor_injury'
      ? 'Minor Injury (paper record)'
      : formType === 'paracetamol_screening'
        ? 'Paracetamol screening (paper record)'
        : 'Patient report (paper record)';

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 print:border-amber-700 print:bg-amber-50">
        <p className="text-xs font-bold text-foreground print:text-gray-900">{title}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground print:text-gray-700 mt-0.5">
          {eventId && <span>Event: {eventId}</span>}
          {incidentId && <span>Incident: {incidentId}</span>}
          <span className="font-mono">{prfId}</span>
          {submittedAt && <span>Recorded: {formatDateTime(submittedAt)}</span>}
          {submittedBy && <span>By: {submittedBy}</span>}
        </div>
      </div>
      <Section title="Paper record" icon={FileText}>
        {renderField('Record notes', notes)}
        {renderField('Recorded by', submittedBy)}
        {renderField('Recorded at', submittedAt ? formatDateTime(submittedAt) : undefined)}
      </Section>
    </div>
  );
}
